import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';

import UserSearch from '@/shared/components/input/UserSearch';
import EmployeeTagMultiSelect from '@/modules/hrm/EmployeeTagMultiSelect';
import TeamMultiSelect from '@/shared/components/input/multiselect/TeamMultiSelect';
import CompanyMultiSelect from '@/shared/components/input/multiselect/CompanyMultiSelect';
import LegalEntityMultiSelect from '@/shared/components/input/multiselect/LegalEntityMultiSelect';
import { hasMultipleCompanies } from '@/shared/components/security/HasMultiCompany';
import HasMultipleLegalEntities from '@/shared/components/security/HasMultipleLegalEntities';
import SavedFiltersButton from '@/shared/components/SavedFiltersButton';
import CrmTypeButtonGroup from '@/shared/components/CrmTypeButtonGroup';
import RelationTypeButtonGroup from '@/shared/components/RelationTypeButtonGroup';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import enumCache from '@/shared/services/EnumCacheService';

import messages from '../hrmMessages';

export const defaultFilters = {
  employeeId: undefined,
  employeeProfile: undefined,
  withEmployeeLabels: [],
  ownCompanies: [],
  legalEntities: [],
  profileId: null,
  teams: [],
  type: [],
  crmTypes: [],
  includeInactive: null,
};

class EmployeeRelationMatrixFilters extends Component {
  state = {
    includeInactiveOptions: [],
  };

  componentDidMount() {
    const { intl } = this.props;

    enumCache.get('inactiveRelationFilter').then(options => {
      this.setState({
        includeInactiveOptions: options.map(x => ({
          id: x.key,
          label: intl.formatMessage(listItemMsg('inactiveRelationFilter', x.key, x.value)),
        })),
      });
    });
  }

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };

    if (JSON.stringify(filters) !== JSON.stringify(newFilters)) {
      onFilterChange(newFilters);
    }
  };

  render() {
    const {
      filters,
      columns,
      intl,
      employeeProfiles,
      onFilterChange,
      showEmployeeAndRoleFilters,
      screen,
      defaultFilters: defaultFiltersProp,
    } = this.props;
    const { includeInactiveOptions } = this.state;

    return (
      <div>
        <div className="row">
          {showEmployeeAndRoleFilters && (
            <>
              <div className="col-lg-3 col-md-6">
                <div className="form-group">
                  <RelationTypeButtonGroup
                    selectedTypes={filters.type || []}
                    onChange={val => this.filterChanged({ type: val })}
                  />
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="form-group">
                  <CrmTypeButtonGroup
                    selectedTypes={filters.crmTypes || []}
                    onChange={val => this.filterChanged({ crmTypes: val })}
                  />
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="form-group">
                  <select
                    className="form-control"
                    value={filters.includeInactive ?? ''}
                    onChange={e =>
                      this.filterChanged({
                        includeInactive: e.target.value ? parseInt(e.target.value) : null,
                      })
                    }
                  >
                    <option value="">
                      {intl.formatMessage(messages.InactiveRelationFilterDefault)}
                    </option>
                    {includeInactiveOptions.map(o => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <EmployeeTagMultiSelect
                value={filters.withEmployeeLabels || []}
                onChange={val => this.filterChanged({ withEmployeeLabels: val })}
                placeholder={intl.formatMessage(messages.EmployeeLabels)}
              />
            </div>
          </div>
          {hasMultipleCompanies() && (
            <div className="col-lg-3 col-md-6">
              <div className="form-group">
                <CompanyMultiSelect
                  selection={filters.ownCompanies || []}
                  onChange={val => this.filterChanged({ ownCompanies: val })}
                />
              </div>
            </div>
          )}
          <HasMultipleLegalEntities>
            <div className="col-lg-3 col-md-6">
              <div className="form-group">
                <LegalEntityMultiSelect
                  selection={filters.legalEntities || []}
                  onChange={val => this.filterChanged({ legalEntities: val })}
                />
              </div>
            </div>
          </HasMultipleLegalEntities>
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <select
                className="form-control"
                value={filters.profileId || ''}
                onChange={e => this.filterChanged({ profileId: e.target.value || null })}
              >
                <option value="">{intl.formatMessage(messages.AllProfiles)}</option>
                {(employeeProfiles || []).map(profile => (
                  <option key={profile.id} value={profile.id}>
                    {profile.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <TeamMultiSelect
                selection={filters.teams || []}
                onChange={val => this.filterChanged({ teams: val })}
              />
            </div>
          </div>
          {showEmployeeAndRoleFilters && (
            <>
              <div className="col-lg-3 col-md-6">
                <div className="form-group">
                  <UserSearch
                    emptyLabel="All employees"
                    onChange={v => this.filterChanged({ employeeId: v?.id })}
                    value={filters.employeeId}
                  />
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="form-group">
                  <select
                    className="form-control"
                    value={filters.employeeProfile ?? ''}
                    onChange={e =>
                      this.filterChanged({ employeeProfile: e.target.value || undefined })
                    }
                  >
                    <option value="">{intl.formatMessage(messages.AllEmployeeRoles)}</option>
                    {columns.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="row">
          <div className="col-lg-12">
            <SavedFiltersButton
              screen={screen || 'AssignmentAnalysisRelation'}
              currentFilters={filters}
              defaultFilters={defaultFiltersProp || defaultFilters}
              onFiltersChanged={onFilterChange}
            />
          </div>
        </div>
      </div>
    );
  }
}

const filtersShape = PropTypes.shape({
  employeeId: PropTypes.string,
  employeeProfile: PropTypes.string,
  withEmployeeLabels: PropTypes.arrayOf(PropTypes.string),
  ownCompanies: PropTypes.arrayOf(PropTypes.string),
  legalEntities: PropTypes.arrayOf(PropTypes.string),
  profileId: PropTypes.string,
  teams: PropTypes.arrayOf(PropTypes.string),
  type: PropTypes.arrayOf(PropTypes.number),
  crmTypes: PropTypes.arrayOf(PropTypes.number),
  includeInactive: PropTypes.number,
});

EmployeeRelationMatrixFilters.propTypes = {
  intl: intlShape.isRequired,
  filters: filtersShape,
  defaultFilters: filtersShape,
  onFilterChange: PropTypes.func.isRequired,
  employeeProfiles: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, label: PropTypes.string }),
  ),
  columns: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, label: PropTypes.string })),
  showEmployeeAndRoleFilters: PropTypes.bool,
  screen: PropTypes.string,
};

EmployeeRelationMatrixFilters.defaultProps = {
  filters: { ...defaultFilters },
  defaultFilters: { ...defaultFilters },
  employeeProfiles: [],
  columns: [],
  showEmployeeAndRoleFilters: true,
  screen: 'AssignmentAnalysisRelation',
};
export default injectIntl(EmployeeRelationMatrixFilters);
